import { OrderMode, PRODUCT_TYPE_NUM, ALERT_TYPE, MarketIDMap, FEE_SCHEDULES_STRINGS } from '@app/_shared/shared.enums';
import {
    CANDLE_OPTIONS, PRODUCT_TYPE, REACTIVATE_OPTIONS, EXECUTION_STYLE, StrategyFormBuildMode, PRICE_METHOD,
    ChartType, COMPARATORS, IndicatorDisplayType, EXECUTION_STYLE_NUM, CANDLE_MINUTES_MAPPING,
    PRODUCT_TYPE_TWO_WAY, PRICE_METHOD_MAPPING, REACTIVE_OPTIONS_MAPPING,
} from '@app/_shared/modules/strategy-form/strategy.enums';
import {
    DefineStrategyResultI, SetupFormResultI, IndicatorLogicI, EntryFormReturnI, ExitFormReturnI, SupportedIndicatorI,
    IndicatorParamI, BackTestInterface, AlgoIdMapping, StrategySocketUpdateI, ActiveTerminatedAlgo, StrategyParams,
    IndicatorRowI, IndicatorDataI, BaseAlsoParams, ExternalAlgoModelI
} from './strategies.interface';
import { SearchSymbolResults, SymbolSearchService } from '../symbol-search';
import { ADVANCE_INDICATOR_INPUT_TYPE } from
    '@app/_shared/modules/strategy-form/components/advance-indicator-form/advance-indicator-form.enums';
import { AlgoSymbolPosition } from '../algowise-position-data/algo-symbol-position.model';
import * as moment from 'moment';
import {
    MOMENT_DATE_FORMAT, MOMENT_TIME_FORMAT, CURRENCY_LABEL, DEFAULT_PRECISION, MAXIMUM_MULTIPLIER, MINIMUM_MULTIPLIER, STRATEGY_CREATION_METHOD
} from '@app/_helpers/constants';
import { find, clone } from 'lodash-es';
import { StrategyStatus } from '../../../../utProto/enums_pb';
import { Utils } from '@app/_helpers';
import { getLocale } from '@app/_shared/pipes/locale-converter/locale-converter.pipe';
import { DURATION_PERIOD_LABELS } from '@app/modules/trading/modules/backtest-detail/modules/algo-detail-fields-form/algo-detail-fields-form.consts';


export class MarketPlaceSettings implements BaseAlsoParams {
    // Setup
    algoName: string;
    algoNotes: string;

    // Default Parameters
    minimumCapital = 1;
    minimumCapitalOnExpiry = 1;
    public = false;
    monthlyFee = 0;
    feeSchedule = FEE_SCHEDULES_STRINGS.MONTHLY;
    minimumMultiplier = MINIMUM_MULTIPLIER;
    maximumMultiplier = MAXIMUM_MULTIPLIER;
    variableFee = 0;
    tags: string[] = [];
}

export class BaseStrategy extends MarketPlaceSettings implements BaseAlsoParams {
    // Automatic Fields
    id?: string;

    // Only in case of SO based strategy
    apiID = -1;

    // Type of algo
    creationMethod = STRATEGY_CREATION_METHOD.INDICATOR_BASED;

    createdAt: number;

    startTime: string;
    startDate: string;
    startTimestamp: number;

    closeTime: string;
    closeDate: string;
    closeTimestamp: number;

    squareOffTime: string;
    allowedDays?: number[];

    get isExternalStrategy() {
        return (this.creationMethod === STRATEGY_CREATION_METHOD.CPP_TYPE) || (this.creationMethod === STRATEGY_CREATION_METHOD.REST_TYPE);
    }
    /**
     * Function To Update Market Place Settings. Can be applicable for all creation Methods
     *
     * @param {BaseAlsoParams} settings
     * @memberof BaseStrategy
     */
    setMarketPlaceSettings(settings: BaseAlsoParams) {
        this.algoName = settings.algoName;
        this.algoNotes = settings.algoNotes;
        this.minimumCapital = settings.minimumCapital;
        this.public = settings.public;
        this.monthlyFee = settings.monthlyFee;
        this.feeSchedule = settings.feeSchedule;
        this.minimumMultiplier = settings.minimumMultiplier;
        this.maximumMultiplier = settings.maximumMultiplier;
        this.variableFee = settings.variableFee;
        this.tags = settings.tags;
    }
}

export class ExternalStrategy extends BaseStrategy implements ExternalAlgoModelI {
    creationMethod = STRATEGY_CREATION_METHOD.REST_TYPE;
    soFileName = null;

    constructor(rawStrategy?: ExternalAlgoModelI) {
        super();

        if (rawStrategy) {
            this.parseRawStrategy(rawStrategy);
        }
    }

    parseRawStrategy(rawStrategy: ExternalAlgoModelI) {
        Object.keys(rawStrategy).forEach((key) => {
            this[key] = rawStrategy[key];
        })
    }
}

export class AlgoStrategy extends BaseStrategy implements DefineStrategyResultI, SetupFormResultI {
    creationMethod = STRATEGY_CREATION_METHOD.INDICATOR_BASED;
    isStarred = false;
    rowSymbols?: SearchSymbolResults[];
    terminatedAt: number;
    algoIDs: AlgoIdMapping[];

    strategyRunningType?: number;


    strategyMode: StrategyFormBuildMode = StrategyFormBuildMode.REGULAR;
    executionStyle: EXECUTION_STYLE;

    symbols: {
        market_id: string,
        security_id: number;
        // Token ID will already be there in algos which are created. (Useful when editing strategy)
        token_id?: number;
    }[] = [];

    // Define
    orderMode: OrderMode;
    isLot: boolean; // Lot/Value
    valueConvertedLot: number[] = [] // The number of lots calculated by entered value (Total Value/That time's LTP) RA-321
    orderQty: number;
    candleSize: CANDLE_OPTIONS;
    productType: PRODUCT_TYPE;
    entryPriceMethod: PRICE_METHOD;
    algoCycles: number;
    chartType: ChartType;
    enablePositionProtection: boolean;
    exitPriceMethod: PRICE_METHOD;
    positionExitTime: number | Date;
    positionRetakeTime: number | Date;
    rerunAlgo: REACTIVATE_OPTIONS;
    switchManually: boolean;

    // Entry
    entryIndicatorRow: IndicatorLogicI;

    // Exit
    isPercent: boolean;
    exitIndicatorRow: IndicatorLogicI;
    stopLoss: string;
    takeProfit: string;
    trailingTicks: string;
    enableTrailingTicks: boolean;

    entryIndicatorString: string;
    exitIndicatorString: string;
    isConditionalExit: boolean;

    // Position Data
    positionObject?: AlgoSymbolPosition;

    parseForm(
        strategyMode: StrategyFormBuildMode,
        executionStyle: EXECUTION_STYLE,
        setup: SetupFormResultI,
        define: DefineStrategyResultI,
        entryForm: EntryFormReturnI,
        exitForm: ExitFormReturnI,
        symbolSearchService: SymbolSearchService
    ) {
        this.strategyMode = strategyMode;
        this.executionStyle = executionStyle;

        this.algoName = setup.algoName;
        this.algoNotes = setup.algoNotes;
        this.symbols = setup.symbols;

        this.orderMode = define.orderMode;
        this.isLot = define.isLot;
        this.orderQty = define.orderQty;
        this.candleSize = define.candleSize;
        this.productType = define.productType;
        this.chartType = define.chartType;
        this.rerunAlgo = define.rerunAlgo;
        this.algoCycles = define.algoCycles;
        this.switchManually = define.switchManually;
        this.startTime = define.startTime;
        this.startDate = define.startDate;
        this.closeTime = define.closeTime;
        this.closeDate = define.closeDate;
        this.calculateTimeStamps();
        this.enablePositionProtection = define.enablePositionProtection;
        this.positionRetakeTime = define.positionRetakeTime;
        this.positionExitTime = define.positionExitTime;
        this.entryIndicatorRow = {
            indicatorRow: entryForm.indicatorRow,
            rowComparator: entryForm.rowComparator
        }
        this.entryPriceMethod = define.entryPriceMethod;
        this.exitIndicatorRow = {
            indicatorRow: exitForm.indicatorRow,
            rowComparator: exitForm.rowComparator
        }
        this.exitPriceMethod = define.exitPriceMethod;
        this.isPercent = exitForm.isPercent;
        this.stopLoss = exitForm.stopLoss;
        this.takeProfit = exitForm.takeProfit;
        this.trailingTicks = exitForm.trailingTicks;
        this.enableTrailingTicks = exitForm.enableTrailingTicks;

        this.allowedDays = define.allowedDays;

        this.generateIndicatorStrings(symbolSearchService);
    }

    generateIndicatorStrings(symbolSearchService: SymbolSearchService) {
        this.entryIndicatorString = this.generateStr(this.entryIndicatorRow, false, symbolSearchService);
        this.isConditionalExit = !!this.exitIndicatorRow.indicatorRow;
        this.exitIndicatorString = this.generateStr(this.exitIndicatorRow, true, symbolSearchService);
    }

    calculateTimeStamps() {
        if (!this.startDate || !this.closeDate) {
            this.startDate = moment().format(MOMENT_DATE_FORMAT);
            this.closeDate = moment().format(MOMENT_DATE_FORMAT);
        }
        this.closeTimestamp = moment(this.closeDate + " " + this.closeTime, MOMENT_DATE_FORMAT + ' ' + MOMENT_TIME_FORMAT).unix();
        this.startTimestamp = moment(this.startDate + " " + this.startTime, MOMENT_DATE_FORMAT + ' ' + MOMENT_TIME_FORMAT).unix();
    }

    parseJSON(data: any | object, symbolSearchService: SymbolSearchService) {
        Object.keys(data).forEach(key => {
            this[key] = data[key]
        });

        this.calculateTimeStamps();
        symbolSearchService.onLoad.subscribe((loaded) => {
            if (loaded) {
                this.entryIndicatorString = this.generateStr(this.entryIndicatorRow, false, symbolSearchService);
                this.exitIndicatorString = this.generateStr(this.exitIndicatorRow, true, symbolSearchService);
            }
        })
        if (this.exitIndicatorRow) {
            this.isConditionalExit = !!this.exitIndicatorRow.indicatorRow;
        }
        this.symbols = this.symbols.map(symbol => {
            return {
                market_id: symbol.market_id,
                security_id: symbol.security_id,
                token_id: symbol.token_id
            }
        })
    }

    generateStr(indicatorLogic: IndicatorLogicI, exit = false, symbolSearchService: SymbolSearchService) {
        try {
            const percentSign = this.isPercent && '%' || ''; // If exit values are in percent add '%' sign otherwise ''
            let partString = ' ';
            let orderMode = this.orderMode.toUpperCase();
            if (exit) {
                if (orderMode === 'BUY') {
                    orderMode = 'SELL';
                } else if (orderMode === 'SELL') {
                    orderMode = 'BUY';
                }
            }
            const formattedQty = getLocale(this.orderQty, this.isLot ? 0 : 2);
            let retStr = `<span class="${orderMode}">${orderMode}</span> ${this.isLot ? formattedQty + ' share(s)'
                : `share(s) for ${CURRENCY_LABEL}` + formattedQty} when `;
            if (indicatorLogic?.indicatorRow && Object.keys(indicatorLogic)?.length) {
                indicatorLogic.indicatorRow.forEach((row, idx) => {
                    row.indicators.forEach(indicator => {
                        if (indicator.type === ADVANCE_INDICATOR_INPUT_TYPE.INDICATOR) {

                            retStr += this.strategyMode === StrategyFormBuildMode.WIZARD ?
                                this.generateWizardModeString(indicator, symbolSearchService) :
                                this.generateProModeString(indicator, row, symbolSearchService);

                        } else {
                            retStr += (`${indicator.type == ADVANCE_INDICATOR_INPUT_TYPE.NUMBER ?
                                `<span class= "indicator">${indicator.name}</span>` : indicator.name}` + ' ');
                        }
                    })

                    if (idx < (indicatorLogic.indicatorRow.length - 1) && (indicatorLogic.rowComparator.length)) {
                        retStr += (indicatorLogic.rowComparator[idx] + ' ')
                    }
                })
                partString = ' OR '
            }

            partString = partString + ` <span class= "stop-loss">Stop Loss</span> of ${this.stopLoss}${percentSign}
      OR <span class= "target-profit">Target Profit</span> of ${this.takeProfit}${percentSign} `;
            if (exit) {
                retStr = retStr + partString;
            }
            return retStr;
        } catch (_) {
            return '';
        }
    }

    generateWizardModeString(indicator: IndicatorDataI, symbolSearchService: SymbolSearchService) {
        let retStr = `<span class="indicator">${indicator.name.toUpperCase()}</span>` + (indicator.params.length
            ? `<span class="indicator">(${indicator.params.join()}) </span>` : ' ');
        if (indicator.symbol) {
            retStr += `of ${symbolSearchService.findScripsBySecurity([indicator.symbol])[0].scrip_name} `;
        }

        return retStr;
    }

    generateProModeString(indicator, row: IndicatorRowI, symbolSearchService: SymbolSearchService) {
        let retStr = `<span class="indicator">${indicator.name.toUpperCase()}</span>` + (indicator.params.length
            ? `<span class="indicator">(${indicator.params.join()}) </span>` : ' ');
        // if (row.symbol) {
        //     retStr += `of ${symbolSearchService.findScripsBySecurity([row.symbol])[0].scrip_name} `;
        // }

        return retStr;
    }

    updateValues(algo: AlgoStrategy) {
        Object.keys(algo).forEach(key => {
            this[key] = algo[key];
        })
    }

    hasAlgoID(id: number) {
        return find(this.algoIDs, (algoID) => algoID.strategy_id === id);
    }

    hasTokenID(id: number) {
        return find(this.algoIDs, (algoID) => algoID.admin_token_id === id);
    }

    /**
     * This function is called while saving algo.
     * It returns copy of algo and removing fields which need not be saved in DB.
     *
     * @returns AlgoStrategy Copy
     * @memberof AlgoStrategy
     */
    getCleanedStructure() {
        const copy = clone(this);
        delete copy.algoIDs;
        return copy;
    }

    updateAlgoState(update: StrategySocketUpdateI) {
        const idToUpdate = this.algoIDs.find((id) => id.admin_token_id = update.old_token_id);
        if (idToUpdate) {
            idToUpdate.strategy_id = update.basketId;
            idToUpdate.admin_token_id = update.tokenId;
        }
    }
}


export class Indicator implements SupportedIndicatorI {
    _id: {
        $oid: string;
    };
    description: string;
    group: string;
    isCandleIntervalRequired: boolean;
    isValueComparisonRequired: boolean;
    isPreviousCandleParamsRequired: boolean;
    isScripNameRequired: boolean;
    name: string;
    numberOfInputParams: number;
    numberOfOptionalParams: number;
    numberOfOutputParams: number;
    inputParamList?: IndicatorParamI[];
    optionalParamList?: IndicatorParamI[];
    alias?: string;
    outputParamList: {
        description: string,
        displayType: IndicatorDisplayType,
        flag: string,
        name: string,
        type: string
    }[];

    inputParamterObjects: InputParameter[] = [];

    constructor(indicator: SupportedIndicatorI) {
        Object.keys(indicator).forEach(key => {
            this[key] = indicator[key];
        });

        if (this.inputParamList) {
            this.inputParamterObjects = this.inputParamList
                .filter(p => p.displayType != IndicatorDisplayType.NOT_REQUIRED)
                .map(param => new InputParameter(param));
        }

        if (this.optionalParamList) {
            this.inputParamterObjects = this.inputParamterObjects.concat(
                this.optionalParamList
                    .filter(p => p.displayType != IndicatorDisplayType.NOT_REQUIRED)
                    .map(param => new InputParameter(param, true))
            );
        }

        // Commented as HFT might need this option but it is not shown in streak.

        // if (this.inputParamterObjects.length > 1) {
        //   this.inputParamterObjects.push(new InputParameter({
        //     defaultValue: 0,
        //     description: '',
        //     displayName: 'output',
        //     displayType: IndicatorDisplayType.DROPDOWN,
        //     name: 'out',
        //     possibleValues: this.outputParamList.map(i => i.name),
        //   }))
        // }
    }
}

export class InputParameter {
    isSelect = false;
    selectOptions = [];
    defaultValue: any;
    name: string;
    isOptional = false;
    displayName: string;
    displayType: IndicatorDisplayType;
    max_value: number;
    min_value: number;

    constructor(param: IndicatorParamI, isOptional = false) {
        this.isOptional = isOptional;
        this.displayName = param.displayName || param.name;
        this.name = param.name;
        this.isSelect = !!(param?.possibleValues?.length);
        this.selectOptions = param.possibleValues?.length && param.possibleValues.sort() || [];
        this.displayType = param.displayType;
        this.max_value = param.max_value;
        this.min_value = param.min_value;
        this.defaultValue = param?.valueUsed?.length ? param.valueUsed[0] : param?.defaultValue || (param?.possibleValues?.length && param.possibleValues[0]);
    }
}


export class BacktestModel implements BackTestInterface {
    AlgoName?= ''
    initialCapital = 0;
    orderQty = 0;
    start = 0;
    startDate: Date;
    startTime: Date;
    end = 0;
    endDate: Date;
    endTime: Date;
    chartType = '';
    candleSize = '';
    stopLoss = 0;
    takeProfit = 0;
    symbols: {
        security_id: number;
        market_id: string;
        token_id?: number;
    }[];
    symbol: string[];
    status: number
    token_id: number;
    Status: number;
    UniquePortfolioId: string;
    webApiStrategyResponseType: number;
    btStartTime: number;

    constructor(backTestObj: BackTestInterface) {
        Object.keys(backTestObj).forEach(key => {
            this[key] = backTestObj[key];
        });
    }

}

export const defaultBackTestParams = {
    duration: DURATION_PERIOD_LABELS.ONE_MONTH,
    initialCapital: 10000000,
    startDate: moment().subtract(1, 'day').subtract(1, 'month').startOf('day').toDate(),
    startTime: moment('09:00 AM', MOMENT_TIME_FORMAT).subtract(1, 'day').subtract(1, 'month').toDate(),
    endDate: moment().subtract(1, 'day').startOf('day').toDate(),
    endTime: moment('03:30 PM', MOMENT_TIME_FORMAT).subtract(1, 'day').toDate()
};

export const defaultAlgoParams = new AlgoStrategy();
defaultAlgoParams.parseForm(
    StrategyFormBuildMode.REGULAR,
    EXECUTION_STYLE.FULLY_AUTOMATED,
    {
        algoName: null,
        symbols: [],
        algoNotes: null,
        feeSchedule: FEE_SCHEDULES_STRINGS.MONTHLY,
        maximumMultiplier: MAXIMUM_MULTIPLIER,
        minimumCapital: 0,
        minimumCapitalOnExpiry: 0,
        minimumMultiplier: MINIMUM_MULTIPLIER,
        monthlyFee: 0,
        public: false,
        tags: [],
        variableFee: 0
    }, {
    candleSize: CANDLE_OPTIONS.fifteenM,
    orderMode: OrderMode.BUY,
    productType: PRODUCT_TYPE.INTRADAY,
    isLot: true,
    rerunAlgo: REACTIVATE_OPTIONS.never,
    switchManually: true,
    orderQty: 1,
    chartType: ChartType.CANDLESTICK,
    entryPriceMethod: PRICE_METHOD.MARKET_ORDER,
    exitPriceMethod: PRICE_METHOD.MARKET_ORDER,
    algoCycles: 1,
    closeDate: moment().format(MOMENT_DATE_FORMAT),
    closeTime: '15:30:00',
    enablePositionProtection: false,
    positionExitTime: moment('11:59 PM', MOMENT_TIME_FORMAT).toDate(),
    positionRetakeTime: moment('12:00 AM', MOMENT_TIME_FORMAT).toDate(),
    startDate: moment().format(MOMENT_DATE_FORMAT),
    startTime: '9:15:00'
}, {
    indicatorRow: [{
        indicators: [
            { name: "LTP", type: ADVANCE_INDICATOR_INPUT_TYPE.INDICATOR, params: [] },
            { name: COMPARATORS.greaterThan, type: ADVANCE_INDICATOR_INPUT_TYPE.COMPARATOR, params: [] },
            { name: "1", type: ADVANCE_INDICATOR_INPUT_TYPE.NUMBER, params: [] }
        ]
    }],
    rowComparator: [],
},
    {
        indicatorRow: [],

        rowComparator: [],
        stopLoss: '1',
        takeProfit: '1',
        trailingTicks: null,
        enableTrailingTicks: false,
        isPercent: true
    },
    null
)

export class RunningCompletedStrategy implements ActiveTerminatedAlgo {
    AlgoName = 'NA';
    ClientID = '';
    EndTime = 0;
    OrderExecutionType = 0;
    PlatformType = 0;
    PreviousPositions = [];
    SequenceNumber = 0;
    StartTime = 0;
    StrategyID = 0;
    StrategyParams: StrategyParams;
    StrategyRunningType = '';
    StrategyStatus: number;
    StrategyType = 0;
    TerminationCode = 0;
    TerminationTime = 0;
    TerminationReasonText = '';
    Timestamp = 0;
    TokenID = 0;
    UniqueAlgoName = '';
    Version = 0;
    _id: {
        $oid: ''
    };
    // Position Data
    positionObject?: AlgoSymbolPosition;

    constructor(obj: ActiveTerminatedAlgo, private symbolSearchService: SymbolSearchService) {
        Object.keys(obj).forEach(key => {
            this[key] = obj[key];
        });

        this.StrategyStatus = obj.StrategyStatus ?? StrategyStatus.STRATEGYSTATUS_RUNNING;

        if (!this.StrategyParams.algoCycles) {
            this.StrategyParams.algoCycles = 1;
        }

        /**
         * The model object is called multiple times due to the nature of observables (share()),
         * since we are using enums (2 ways) for mapping, in case 1 -> Intraday enum
         * this Intraday will be mapped back to 1 on second pass, which is not desirable.
         *
         * TODO: Refactor with better mapping logic without using 2 way mapping (throughout), or
         * manage this observable nature
         */
        this.Timestamp = Utils.manageTimestamp(this.Timestamp);
        this.TerminationTime = Utils.manageTimestamp(this.TerminationTime);
        this.OrderExecutionType = EXECUTION_STYLE[EXECUTION_STYLE_NUM[this.OrderExecutionType]] ?? this.OrderExecutionType;
        this.StrategyParams.productType = typeof (this.StrategyParams.productType) === 'string' ?
            this.StrategyParams.productType : PRODUCT_TYPE_NUM[this.StrategyParams.productType];
        this.StrategyRunningType = typeof (this.StrategyRunningType) === 'string' ?
            this.StrategyRunningType : ALERT_TYPE[this.StrategyRunningType];
    }

    /**
     * FOR RA-1000
     * Adding lot size from the symbol to get
     * converted lot value.
     */
    getLotSize(element): number {
        const symbolInfo = this.symbolSearchService.symbolInfo(
            `${MarketIDMap[element.StrategyParams?.symbol?.market_id]}_${element.StrategyParams?.symbol?.security_id}`) || [1];
        return symbolInfo[0];
    }

    divideByLotSize(element, _this) {
        return element / this.getLotSize(_this);
    }

    mapToAlgoStrategy(symbolSearchService: SymbolSearchService, backTestDeploy = false, isReactivate = false): AlgoStrategy {
        const priceCalculationParams = this.StrategyParams.entryCondition?.priceCalculationParams;

        const AlgoStrategyObj = new AlgoStrategy();
        AlgoStrategyObj.strategyRunningType = ALERT_TYPE[this.StrategyRunningType];
        AlgoStrategyObj.algoName = this.AlgoName;
        AlgoStrategyObj.symbols = [this.StrategyParams.symbol];
        AlgoStrategyObj.createdAt = this.Timestamp;

        // TODO: Re check when creator form is revampled.
        AlgoStrategyObj.feeSchedule = FEE_SCHEDULES_STRINGS.MONTHLY,
            AlgoStrategyObj.maximumMultiplier = MAXIMUM_MULTIPLIER;
        AlgoStrategyObj.minimumCapital = 0;
        AlgoStrategyObj.minimumMultiplier = MINIMUM_MULTIPLIER;
        AlgoStrategyObj.monthlyFee = 0;
        AlgoStrategyObj.public = false;
        AlgoStrategyObj.tags = [];
        AlgoStrategyObj.variableFee = 0;

        AlgoStrategyObj.algoCycles = this.StrategyParams.algoCycles;
        AlgoStrategyObj.orderMode = this.StrategyParams.orderMode ? OrderMode.SELL : OrderMode.BUY;
        AlgoStrategyObj.isLot = !(!!this.StrategyParams.qtyType);
        AlgoStrategyObj.orderQty = AlgoStrategyObj.isLot ? this.divideByLotSize(this.StrategyParams.orderQtyValue, this) : (backTestDeploy ? this.StrategyParams.orderQtyValue : +Utils.precisionUtil(this.StrategyParams.orderQtyValue, DEFAULT_PRECISION));
        AlgoStrategyObj.valueConvertedLot = isReactivate ? this.StrategyParams.valueConvertedLot : [AlgoStrategyObj.isLot ? AlgoStrategyObj.orderQty : this.divideByLotSize(AlgoStrategyObj.orderQty, this)];
        AlgoStrategyObj.candleSize = CANDLE_MINUTES_MAPPING[this.StrategyParams.candleSize];
        AlgoStrategyObj.productType = PRODUCT_TYPE_TWO_WAY[this.StrategyParams.productType];

        AlgoStrategyObj.startTime = moment(new Date(this.StrategyParams.startTime)).format(MOMENT_TIME_FORMAT);
        AlgoStrategyObj.startDate = moment(new Date(this.StrategyParams.startTime)).format(MOMENT_DATE_FORMAT);
        AlgoStrategyObj.startTimestamp = Math.floor(this.StrategyParams.startTime * 1e-3);

        AlgoStrategyObj.closeTime = moment(new Date(this.StrategyParams.endTime)).format(MOMENT_TIME_FORMAT);
        AlgoStrategyObj.closeDate = moment(new Date(this.StrategyParams.endTime)).format(MOMENT_DATE_FORMAT);
        AlgoStrategyObj.closeTimestamp = this.StrategyParams.endTime * 1e-3;
        AlgoStrategyObj.entryPriceMethod = PRICE_METHOD_MAPPING[priceCalculationParams?.priceMethod || 0];
        AlgoStrategyObj.chartType = this.StrategyParams.chartType as any;
        AlgoStrategyObj.enablePositionProtection = this.StrategyParams.enablePositionProtection;
        AlgoStrategyObj.exitPriceMethod = PRICE_METHOD_MAPPING[priceCalculationParams?.priceMethod || 0];
        AlgoStrategyObj.positionExitTime = this.StrategyParams.positionExitTime;
        AlgoStrategyObj.positionRetakeTime = this.StrategyParams.positionRetakeTime;
        AlgoStrategyObj.rerunAlgo = REACTIVE_OPTIONS_MAPPING[this.StrategyParams.reactivateInterval];
        AlgoStrategyObj.switchManually = false

        AlgoStrategyObj.entryIndicatorRow = this.StrategyParams.entryIndicatorRow;
        AlgoStrategyObj.entryIndicatorString = this.StrategyParams.entryIndicatorString;
        AlgoStrategyObj.exitIndicatorRow = this.StrategyParams.exitIndicatorRow;
        AlgoStrategyObj.exitIndicatorString = this.StrategyParams.exitIndicatorString;
        AlgoStrategyObj.executionStyle = this.StrategyParams.executionStyle as any;
        AlgoStrategyObj.isPercent = this.StrategyParams.isPercent;
        AlgoStrategyObj.isStarred = this.StrategyParams.isStarred;
        AlgoStrategyObj.stopLoss = this.StrategyParams.stopLoss;
        AlgoStrategyObj.takeProfit = Utils.precisionUtil(priceCalculationParams?.targetProfit,
            DEFAULT_PRECISION)
        AlgoStrategyObj.strategyMode = this.StrategyParams.strategyMode as any;

        AlgoStrategyObj.entryIndicatorString = AlgoStrategyObj.generateStr(AlgoStrategyObj?.entryIndicatorRow, false, symbolSearchService);
        AlgoStrategyObj.isConditionalExit = !!AlgoStrategyObj?.exitIndicatorRow?.indicatorRow;
        AlgoStrategyObj.exitIndicatorString = AlgoStrategyObj.generateStr(AlgoStrategyObj?.exitIndicatorRow, true, symbolSearchService);
        return AlgoStrategyObj;
    }

    get valueConvertedLotsOrOrderQtyValue() {
        return (this.StrategyParams?.valueConvertedLot?.length && this.StrategyParams.valueConvertedLot[0]) ?
            this.StrategyParams.valueConvertedLot[0] : this.StrategyParams.orderQtyValue
    }

}
